import React, { useMemo, useState } from 'react';
import { Card, Select, Space, Tabs, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { projectService } from '@/services/project.service';
import { ExecutionPackagesPanel } from '@/pages/epc/execution/ExecutionPackagesPanel';
import { WorkAreasPanel } from '@/pages/epc/execution/WorkAreasPanel';
import { WorkFrontsPanel } from '@/pages/epc/execution/WorkFrontsPanel';
import { ProgressPanel } from '@/pages/epc/execution/ProgressPanel';
import { ConstraintsPanel } from '@/pages/epc/execution/ConstraintsPanel';
import { PermitsPanel } from '@/pages/epc/execution/PermitsPanel';
import { FieldDocumentsPanel } from '@/pages/epc/execution/FieldDocumentsPanel';
import { FieldPhotosPanel } from '@/pages/epc/execution/FieldPhotosPanel';
import { ManpowerPanel } from '@/pages/epc/execution/ManpowerPanel';
import { EquipmentPanel } from '@/pages/epc/execution/EquipmentPanel';
import { DelaysPanel } from '@/pages/epc/execution/DelaysPanel';
import { ProductivityPanel } from '@/pages/epc/execution/ProductivityPanel';
import { ConstructionDashboardPanel } from '@/pages/epc/execution/ConstructionDashboardPanel';
import { WeatherPanel } from '@/pages/epc/execution/WeatherPanel';
import { FieldAiPanel } from '@/pages/epc/execution/FieldAiPanel';
import { AwpPanel } from '@/pages/epc/execution/AwpPanel';
import { DigitalTwinPanel } from '@/pages/epc/execution/DigitalTwinPanel';
import { isCepTabKey, type CepTabKey } from '@/pages/epc/execution/cepTabs';
import { ConstructionCostControlStrip } from '@/pages/epc/execution/ConstructionCostControlStrip';

const { Title, Paragraph } = Typography;

const DEFAULT_TAB: CepTabKey = 'execution-packages';

const EpcConstructionExecutionPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [projectId, setProjectId] = useState<string | undefined>();
  const rawTab = searchParams.get('tab');
  const activeTab: CepTabKey = isCepTabKey(rawTab) ? rawTab : DEFAULT_TAB;

  const projectsQ = useQuery({
    queryKey: ['epc-projects'],
    queryFn: () => projectService.getProjectsForSelection(),
    staleTime: 60_000,
  });

  const projects = useMemo(() => {
    const raw = projectsQ.data;
    return Array.isArray(raw) ? raw : [];
  }, [projectsQ.data]);

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12 }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          Construction Execution
        </Title>
        <Tag color="cyan">CEP · Program 12.1 R2</Tag>
        <Select
          style={{ minWidth: 280 }}
          allowClear
          showSearch
          loading={projectsQ.isLoading}
          placeholder={
            projectsQ.isError
              ? 'Failed to load projects'
              : projects.length === 0 && !projectsQ.isLoading
                ? 'No projects for this company'
                : 'Filter by project'
          }
          optionFilterProp="label"
          value={projectId}
          onChange={(v) => setProjectId(v)}
          options={projects.map((p: any) => ({
            value: String(p.id),
            label: `${p.project_number || p.code || ''} ${p.project_name || p.name || p.id}`.trim(),
          }))}
        />
      </Space>
      <Paragraph type="secondary">
        Package-centric site execution — areas, work fronts, progress, resources, permits, and field
        capture. Advisory only; never bypasses Approval Runtime or Financial Posting. Project Cost
        Budget and Cost Control live under Project (linked below when a project is selected).
      </Paragraph>

      {projectId ? <ConstructionCostControlStrip projectId={projectId} /> : null}

      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          const next = new URLSearchParams(searchParams);
          next.set('tab', key);
          setSearchParams(next, { replace: true });
        }}
        items={[
          {
            key: 'execution-packages',
            label: 'Execution Packages',
            children: <ExecutionPackagesPanel projectId={projectId} />,
          },
          {
            key: 'construction-areas',
            label: 'Construction Areas',
            children: <WorkAreasPanel projectId={projectId} />,
          },
          {
            key: 'work-fronts',
            label: 'Work Fronts',
            children: <WorkFrontsPanel projectId={projectId} />,
          },
          {
            key: 'cep-progress',
            label: 'Progress',
            children: <ProgressPanel projectId={projectId} />,
          },
          {
            key: 'constraints',
            label: 'Constraints',
            children: <ConstraintsPanel projectId={projectId} />,
          },
          {
            key: 'manpower',
            label: 'Manpower',
            children: <ManpowerPanel projectId={projectId} />,
          },
          {
            key: 'equipment',
            label: 'Equipment',
            children: <EquipmentPanel projectId={projectId} />,
          },
          {
            key: 'permits',
            label: 'Permits',
            children: <PermitsPanel projectId={projectId} />,
          },
          {
            key: 'field-docs',
            label: 'Field documents',
            children: <FieldDocumentsPanel projectId={projectId} />,
          },
          {
            key: 'photos',
            label: 'Photos',
            children: <FieldPhotosPanel projectId={projectId} />,
          },
          {
            key: 'delays',
            label: 'Delays',
            children: <DelaysPanel projectId={projectId} />,
          },
          {
            key: 'weather',
            label: 'Weather',
            children: <WeatherPanel projectId={projectId} />,
          },
          {
            key: 'productivity',
            label: 'Productivity',
            children: <ProductivityPanel projectId={projectId} />,
          },
          {
            key: 'dashboards',
            label: 'Dashboards',
            children: <ConstructionDashboardPanel projectId={projectId} />,
          },
          {
            key: 'field-ai',
            label: 'Field AI',
            children: <FieldAiPanel projectId={projectId} />,
          },
          {
            key: 'awp',
            label: 'AWP',
            children: <AwpPanel projectId={projectId} />,
          },
          {
            key: 'digital-twin',
            label: 'Digital Twin',
            children: <DigitalTwinPanel projectId={projectId} />,
          },
        ]}
      />

      {!projectId ? (
        <Card size="small" style={{ marginTop: 12 }}>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            Select a project to load construction execution data for that site.
          </Paragraph>
        </Card>
      ) : null}
    </div>
  );
};

export default EpcConstructionExecutionPage;
